// ─── SLATimer.jsx ────────────────────────────────────────────────────────────
// Live SLA response timer for each incident.
// Shows time elapsed since incident creation — green/amber/red thresholds.
//
// Acknowledge / Resolve are front-end-only workflow actions:
//   - Acknowledge freezes the TIME TO ACKNOWLEDGE clock and flips the label.
//   - Resolve freezes the TIME TO RESOLVE clock and marks the incident done.
// State is local (useState); a page refresh resets it. No backend / persistence.
//
// USAGE — add inside Card() in Incidents.jsx:
//   import SLATimer from './SLATimer';
//   <SLATimer incidentId={inc.id} createdAt={inc.created_at || inc.timestamp} />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/apiClient';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

// Thresholds in minutes
const SLA = { ACK: 15, RESOLVE: 60 };

function getElapsed(createdAt) {
  if (!createdAt) return null;
  try {
    const created = new Date(createdAt);
    if (isNaN(created)) return null;
    return Math.floor((Date.now() - created.getTime()) / 1000); // seconds
  } catch { return null; }
}

// Elapsed between createdAt and a fixed stop time (ms epoch)
function getElapsedFrozen(createdAt, stopMs) {
  if (!createdAt || !stopMs) return null;
  try {
    const created = new Date(createdAt);
    if (isNaN(created)) return null;
    return Math.max(0, Math.floor((stopMs - created.getTime()) / 1000));
  } catch { return null; }
}

function formatDuration(seconds) {
  if (seconds === null) return '--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function getColor(seconds, thresholdMinutes) {
  if (seconds === null) return '#6b7280';
  const mins = seconds / 60;
  if (mins < thresholdMinutes * 0.7) return '#10b981'; // green: under 70%
  if (mins < thresholdMinutes)        return '#f59e0b'; // amber: approaching
  return '#ff3b4e';                                      // red: breached
}

// Compact badge for incident list cards
export function SLABadge({ createdAt }) {
  const [elapsed, setElapsed] = useState(() => getElapsed(createdAt));
  useEffect(() => {
    const t = setInterval(() => setElapsed(getElapsed(createdAt)), 1000);
    return () => clearInterval(t);
  }, [createdAt]);

  const col = getColor(elapsed, SLA.ACK);
  return (
    <span style={{ fontSize: 9, ...mono, color: col, background: `${col}18`, border: `1px solid ${col}33`, padding: '1px 6px', borderRadius: 3 }}>
      ⏱ {formatDuration(elapsed)}
    </span>
  );
}

// Small action button
function ActionButton({ label, onClick, disabled, color }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 10,
        ...mono,
        letterSpacing: 0.5,
        color: disabled ? '#4b5563' : color,
        background: disabled ? '#ffffff06' : `${color}14`,
        border: `1px solid ${disabled ? '#ffffff10' : `${color}44`}`,
        borderRadius: 4,
        padding: '5px 12px',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// Full panel for incident detail view
export default function SLATimer({ incidentId, createdAt: createdAtProp }) {
  const [createdAt] = useState(() => createdAtProp || new Date().toISOString());
  const [nowTick, setNowTick] = useState(0);
  const [ackAtMs, setAckAtMs] = useState(null);
  const [resolvedAtMs, setResolvedAtMs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!incidentId) return;
      try {
        const res = await apiFetch('/incidents/acks');
        if (!res.ok) return;
        const data = await res.json();
        const rec = (data.acks || {})[incidentId];
        if (rec && !cancelled) {
          if (rec.acked_at)    setAckAtMs(new Date(rec.acked_at).getTime());
          if (rec.resolved_at) setResolvedAtMs(new Date(rec.resolved_at).getTime());
        } else if (!cancelled) {
          setAckAtMs(null);
          setResolvedAtMs(null);
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [incidentId]);

  const acknowledged = ackAtMs !== null;
  const resolved     = resolvedAtMs !== null;

  useEffect(() => {
    const t = setInterval(() => setNowTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // ACK elapsed: frozen once acknowledged, otherwise live
  const ackElapsed = acknowledged
    ? getElapsedFrozen(createdAt, ackAtMs)
    : getElapsed(createdAt);

  // RESOLVE elapsed: frozen once resolved, otherwise live
  const resolveElapsed = resolved
    ? getElapsedFrozen(createdAt, resolvedAtMs)
    : getElapsed(createdAt);

  const ackColor     = getColor(ackElapsed, SLA.ACK);
  const resolveColor = getColor(resolveElapsed, SLA.RESOLVE);
  const ackPct       = ackElapsed === null ? 0 : Math.min((ackElapsed / 60 / SLA.ACK) * 100, 100);
  const resolvePct   = resolveElapsed === null ? 0 : Math.min((resolveElapsed / 60 / SLA.RESOLVE) * 100, 100);

  async function handleAcknowledge() {
    if (acknowledged || busy || !incidentId) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/incidents/${incidentId}/ack`, { method: 'POST', body: JSON.stringify({}) });
      if (res.ok) {
        const d = await res.json();
        setAckAtMs(d.acked_at ? new Date(d.acked_at).getTime() : Date.now());
      } else if (res.status === 403) {
        alert('You do not have permission to acknowledge incidents.');
      }
    } catch { /* ignore */ }
    finally { setBusy(false); }
  }

  async function handleResolve() {
    if (resolved || busy || !incidentId) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/incidents/${incidentId}/resolve`, { method: 'POST', body: JSON.stringify({}) });
      if (res.ok) {
        const d = await res.json();
        if (d.acked_at)    setAckAtMs(new Date(d.acked_at).getTime());
        if (d.resolved_at) setResolvedAtMs(new Date(d.resolved_at).getTime());
      } else if (res.status === 403) {
        alert('You do not have permission to resolve incidents.');
      }
    } catch { /* ignore */ }
    finally { setBusy(false); }
  }

  return (
    <div style={{ background: '#0d1018', border: '1px solid #ffffff0a', borderRadius: 6, padding: '10px 12px', marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: '#4b5563', ...mono }}>SLA / RESPONSE TIMER</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton
            label={acknowledged ? '✓ ACKNOWLEDGED' : 'ACKNOWLEDGE'}
            onClick={handleAcknowledge}
            disabled={acknowledged}
            color="#f59e0b"
          />
          <ActionButton
            label={resolved ? '✓ RESOLVED' : 'RESOLVE'}
            onClick={handleResolve}
            disabled={resolved}
            color="#10b981"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {/* Acknowledge SLA */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: '#6b7280', ...mono }}>TIME TO ACKNOWLEDGE</span>
            <span style={{ fontSize: 9, color: '#4b5563', ...mono }}>SLA: {SLA.ACK}min</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: ackColor, ...mono, marginBottom: 6 }}>
            {formatDuration(ackElapsed)}
          </div>
          <div style={{ height: 4, background: '#ffffff0a', borderRadius: 2 }}>
            <div style={{ width: `${ackPct}%`, height: '100%', background: ackColor, borderRadius: 2, transition: 'width 1s linear' }} />
          </div>
          <div style={{ fontSize: 9, color: acknowledged ? '#10b981' : ackColor, ...mono, marginTop: 4 }}>
            {acknowledged ? '✓ Acknowledged' : ackPct >= 100 ? '⚠ SLA BREACHED' : 'Awaiting acknowledgement'}
          </div>
        </div>

        {/* Resolve SLA */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: '#6b7280', ...mono }}>TIME TO RESOLVE</span>
            <span style={{ fontSize: 9, color: '#4b5563', ...mono }}>SLA: {SLA.RESOLVE}min</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: resolveColor, ...mono, marginBottom: 6 }}>
            {formatDuration(resolveElapsed)}
          </div>
          <div style={{ height: 4, background: '#ffffff0a', borderRadius: 2 }}>
            <div style={{ width: `${resolvePct}%`, height: '100%', background: resolveColor, borderRadius: 2, transition: 'width 1s linear' }} />
          </div>
          <div style={{ fontSize: 9, color: resolved ? '#10b981' : resolveColor, ...mono, marginTop: 4 }}>
            {resolved ? '✓ Resolved' : resolvePct >= 100 ? '⚠ SLA BREACHED' : 'In progress'}
          </div>
        </div>
      </div>
    </div>
  );
}